import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, ChevronDown, Loader2, ExternalLink } from "lucide-react";
import api from "@/lib/api";

// Groups the scripture references from a decree set into a collapsible study
// section. Each reference expands to reveal the full verse text, matching the
// cited translation when it is in our public-domain library.
export function ScripturesReferenced({ decrees }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const refs = [];
  const seen = new Set();
  (decrees || []).forEach((d) => {
    if (d.reference && !seen.has(d.reference)) {
      seen.add(d.reference);
      refs.push(d.reference);
    }
  });
  if (refs.length === 0) return null;

  const ensureData = async () => {
    if (data || loading) return;
    setLoading(true);
    try {
      const { data: res } = await api.post("/scriptures/lookup", { refs });
      const map = {};
      (res.results || []).forEach((r) => { map[r.reference] = r; });
      setData(map);
    } catch {
      setData({});
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = () => {
    const next = !open;
    setOpen(next);
    if (next) ensureData();
  };

  const toggleRef = (ref) => setExpanded((p) => ({ ...p, [ref]: !p[ref] }));

  return (
    <div className="mt-12 pt-8 border-t border-[#EAE6DF]" data-testid="scriptures-referenced">
      <button
        data-testid="scriptures-toggle"
        onClick={toggleSection}
        className="w-full flex items-center justify-between gap-3 text-left group"
      >
        <span className="flex items-center gap-2.5">
          <BookOpen className="w-5 h-5 text-[#9B7E4E]" strokeWidth={1.5} />
          <span className="font-heading text-xl text-[#2C2A29]">Scriptures Referenced</span>
          <span className="font-body text-sm text-[#B7AE9F]">({refs.length})</span>
        </span>
        <ChevronDown className={`w-5 h-5 text-[#B7AE9F] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-5 space-y-2.5">
          {refs.map((ref) => {
            const r = data && data[ref];
            const isOpen = !!expanded[ref];
            return (
              <div key={ref} className="border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm overflow-hidden">
                <button
                  data-testid={`scripture-toggle-${ref}`}
                  onClick={() => toggleRef(ref)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#F3F0EA] transition-colors duration-300"
                >
                  <span className="font-body text-sm text-[#2C2A29]">{ref}</span>
                  <ChevronDown className={`w-4 h-4 text-[#B7AE9F] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pt-1 border-t border-[#EAE6DF]" data-testid={`scripture-verse-${ref}`}>
                    {loading && !r ? (
                      <div className="py-3"><Loader2 className="w-4 h-4 animate-spin text-[#B7AE9F]" /></div>
                    ) : r && r.verses && r.verses.length ? (
                      <>
                        <p className="font-decree text-lg text-[#2C2A29] leading-relaxed mt-2">
                          {r.verses.map((v) => (
                            <span key={v.verse}>
                              <sup className="text-[#B7AE9F] mr-1">{v.verse}</sup>
                              {v.text}{" "}
                            </span>
                          ))}
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                          <span className="font-body text-xs text-[#9B7E4E]">
                            {r.substituted
                              ? `${r.requested_translation} isn't in our public-domain library — shown in ${r.shown_translation_name}`
                              : r.shown_translation_name}
                          </span>
                          <button
                            data-testid={`scripture-read-bible-${ref}`}
                            onClick={() => navigate("/bible", { state: { goto: { book: r.book, chapter: r.chapter, verse: r.verse_start } } })}
                            className="flex items-center gap-1.5 font-body text-xs text-[#2C2A29] hover:text-[#9B7E4E] transition-colors duration-300"
                          >
                            Read in Bible <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <p className="font-body text-sm text-[#7A756E] py-2">Verse text unavailable for this reference.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
